import type { SpecialSchedule } from '../types';

const FIELD = 'w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-800 shadow-sm outline-none transition focus:border-[#ee2b74] focus:ring-2 focus:ring-[#ee2b74]/20';

const DAYS = [
    { label: 'Mon', value: 1 }, { label: 'Tue', value: 2 }, { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 }, { label: 'Fri', value: 5 }, { label: 'Sat', value: 6 },
    { label: 'Sun', value: 7 },
];

interface ScheduleSectionProps {
    schedule: SpecialSchedule;
    onChange: (patch: Partial<SpecialSchedule>) => void;
}

export default function ScheduleSection({ schedule, onChange }: ScheduleSectionProps) {
    return (
        <div className="space-y-3">
            {/* Recurrence type */}
            <div>
                <label className="mb-1.5 block text-xs font-semibold text-stone-700">Recurrence Type</label>
                <select
                    value={schedule.recurrence_type}
                    onChange={e => onChange({ recurrence_type: e.target.value as SpecialSchedule['recurrence_type'] })}
                    className={`${FIELD} appearance-none pr-10`}
                >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                </select>
            </div>

            {/* Interval + Timezone */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Interval</label>
                    <input type="number" min={1} value={schedule.interval_count} className={FIELD}
                           onChange={e => onChange({ interval_count: Number(e.target.value) || 1 })} />
                </div>
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Timezone</label>
                    <input type="text" value={schedule.timezone} className={FIELD}
                           onChange={e => onChange({ timezone: e.target.value })} />
                </div>
            </div>

            {/* Starts At + Ends At */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Starts At</label>
                    <input type="time" value={schedule.starts_at} className={FIELD}
                           onChange={e => onChange({ starts_at: e.target.value })} />
                </div>
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Ends At</label>
                    <input type="time" value={schedule.ends_at} className={FIELD}
                           onChange={e => onChange({ ends_at: e.target.value })} />
                </div>
            </div>

            {/* Start Date + End Date */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Start Date</label>
                    <input type="date" value={schedule.start_date} className={FIELD}
                           onChange={e => onChange({ start_date: e.target.value })} />
                </div>
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">End Date</label>
                    <input type="date" value={schedule.end_date} className={FIELD}
                           onChange={e => onChange({ end_date: e.target.value })} />
                </div>
            </div>

            {/* Days of week */}
            {schedule.recurrence_type === 'weekly' && (
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Days of Week</label>
                    <div className="flex flex-wrap gap-2">
                        {DAYS.map(day => {
                            const checked = schedule.days_of_week.includes(day.value);
                            return (
                                <button key={day.value} type="button"
                                        onClick={() => onChange({
                                            days_of_week: checked
                                                ? schedule.days_of_week.filter(d => d !== day.value)
                                                : [...schedule.days_of_week, day.value].sort((a, b) => a - b),
                                        })}
                                        className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition ${
                                            checked
                                                ? 'bg-[#ee2b74] text-white ring-[#ee2b74]'
                                                : 'bg-white text-stone-700 ring-stone-200 hover:bg-stone-50'
                                        }`}>
                                    {day.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Day of month */}
            {schedule.recurrence_type === 'monthly' && (
                <div>
                    <label className="mb-1.5 block text-xs font-semibold text-stone-700">Day of Month</label>
                    <input type="number" min={1} max={31} value={schedule.day_of_month ?? 1}
                           onChange={e => onChange({ day_of_month: Number(e.target.value) })}
                           className={FIELD} />
                </div>
            )}
        </div>
    );
}